import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar, Clock, CheckCircle, X, User, Award } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

interface Enrollment {
  id: string;
  participant_id: string;
  status: string;
  enrollment_date: string;
  completion_date: string | null;
  attendance_hours: number;
  profiles: {
    first_name: string;
    last_name: string;
    email: string;
    position_title?: string;
    department?: string;
    pd_hours_completed?: number;
  };
}

interface Course {
  id: string;
  title: string;
  duration_hours: number;
}

interface CompletionManagerProps {
  courseId: string;
}

export function CompletionManager({ courseId }: CompletionManagerProps) {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedEnrollment, setSelectedEnrollment] = useState<Enrollment | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [completionData, setCompletionData] = useState({
    completion_date: new Date().toISOString().split('T')[0],
    attendance_hours: 0,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [courseId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch course details
      const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .select('id, title, duration_hours')
        .eq('id', courseId)
        .single();

      if (courseError) throw courseError;
      setCourse(courseData);

      // Fetch enrollments with user profiles
      const { data: enrollmentsData, error: enrollmentsError } = await supabase
        .from('enrollments')
        .select(`
          id,
          participant_id,
          status,
          enrollment_date,
          completion_date,
          attendance_hours,
          profiles!enrollments_participant_id_fkey (
            first_name,
            last_name,
            email,
            position_title,
            department,
            pd_hours_completed
          )
        `)
        .eq('course_id', courseId)
        .order('enrollment_date', { ascending: true });

      if (enrollmentsError) throw enrollmentsError;
      setEnrollments(enrollmentsData || []);

      // Set default attendance hours to course duration
      setCompletionData(prev => ({
        ...prev,
        attendance_hours: courseData.duration_hours || 0
      }));

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load course data');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkCompleted = async (enrollment: Enrollment) => {
    setSelectedEnrollment(enrollment);
    setCompletionData({
      completion_date: new Date().toISOString().split('T')[0],
      attendance_hours: course?.duration_hours || 0,
      notes: ''
    });
    setDialogOpen(true);
  };

  const handleMarkIncomplete = async (enrollmentId: string) => {
    try {
      const { error } = await supabase
        .from('enrollments')
        .update({
          status: 'enrolled',
          completion_date: null,
          attendance_hours: 0
        })
        .eq('id', enrollmentId);

      if (error) throw error;

      toast.success('Marked as incomplete');
      fetchData();
    } catch (error) {
      console.error('Error updating enrollment:', error);
      toast.error('Failed to update enrollment');
    }
  };

  const handleSaveCompletion = async () => {
    if (!selectedEnrollment || !course) return;

    try {
      // Update enrollment status
      const { error: enrollmentError } = await supabase
        .from('enrollments')
        .update({
          status: 'completed',
          completion_date: completionData.completion_date,
          attendance_hours: completionData.attendance_hours
        })
        .eq('id', selectedEnrollment.id);

      if (enrollmentError) throw enrollmentError;

      // Update user's PD hours
      const currentPdHours = selectedEnrollment.profiles.pd_hours_completed || 0;
      const newPdHours = currentPdHours + completionData.attendance_hours;

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          pd_hours_completed: newPdHours
        })
        .eq('id', selectedEnrollment.participant_id);

      if (profileError) throw profileError;

      // Log the completion activity
      const { error: activityError } = await supabase
        .from('activity_logs')
        .insert({
          user_id: selectedEnrollment.participant_id,
          action: 'course_completed',
          resource_type: 'course',
          resource_id: courseId,
          details: {
            course_title: course.title,
            completion_date: completionData.completion_date,
            hours_earned: completionData.attendance_hours,
            marked_by: user?.id,
            notes: completionData.notes
          }
        });

      if (activityError) {
        console.warn('Failed to log activity:', activityError);
      }

      toast.success(`${selectedEnrollment.profiles.first_name} ${selectedEnrollment.profiles.last_name} marked as completed`);
      
      setDialogOpen(false);
      setSelectedEnrollment(null);
      fetchData();
    } catch (error) {
      console.error('Error saving completion:', error);
      toast.error('Failed to save completion');
    }
  };

  if (loading) {
    return <div className="p-4">Loading enrollments...</div>;
  }

  const enrolledUsers = enrollments.filter(e => e.status === 'enrolled');
  const completedUsers = enrollments.filter(e => e.status === 'completed');

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Enrolled Users ({enrolledUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {enrolledUsers.length === 0 ? (
              <p className="text-muted-foreground">No enrolled users to complete</p>
            ) : (
              <div className="space-y-3">
                {enrolledUsers.map((enrollment) => (
                  <div key={enrollment.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">
                        {enrollment.profiles.first_name} {enrollment.profiles.last_name}
                      </p>
                      <p className="text-sm text-muted-foreground">{enrollment.profiles.email}</p>
                      <p className="text-xs text-muted-foreground">
                        {enrollment.profiles.position_title && `${enrollment.profiles.position_title} • `}
                        Enrolled: {new Date(enrollment.enrollment_date).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleMarkCompleted(enrollment)}
                      className="ml-3"
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Mark Complete
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5" />
              Completed Users ({completedUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {completedUsers.length === 0 ? (
              <p className="text-muted-foreground">No completed users yet</p>
            ) : (
              <div className="space-y-3">
                {completedUsers.map((enrollment) => (
                  <div key={enrollment.id} className="flex items-center justify-between p-3 border rounded-lg bg-green-50">
                    <div className="flex-1">
                      <p className="font-medium">
                        {enrollment.profiles.first_name} {enrollment.profiles.last_name}
                      </p>
                      <p className="text-sm text-muted-foreground">{enrollment.profiles.email}</p>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-xs text-muted-foreground">
                          Completed: {enrollment.completion_date ? new Date(enrollment.completion_date).toLocaleDateString() : 'N/A'}
                        </span>
                        <Badge variant="secondary" className="text-xs">
                          {enrollment.attendance_hours}h earned
                        </Badge>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleMarkIncomplete(enrollment.id)}
                      className="ml-3"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Mark Incomplete
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* All Enrollments Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Enrollments</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Position</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Enrolled Date</TableHead>
                <TableHead>Completion Date</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {enrollments.map((enrollment) => (
                <TableRow key={enrollment.id}>
                  <TableCell className="font-medium">
                    {enrollment.profiles.first_name} {enrollment.profiles.last_name}
                  </TableCell>
                  <TableCell>{enrollment.profiles.email}</TableCell>
                  <TableCell>{enrollment.profiles.position_title || 'N/A'}</TableCell>
                  <TableCell>
                    <Badge variant={enrollment.status === 'completed' ? 'default' : 'secondary'}>
                      {enrollment.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {new Date(enrollment.enrollment_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {enrollment.completion_date 
                      ? new Date(enrollment.completion_date).toLocaleDateString() 
                      : '-'
                    }
                  </TableCell>
                  <TableCell>{enrollment.attendance_hours}h</TableCell>
                  <TableCell>
                    {enrollment.status === 'enrolled' ? (
                      <Button
                        size="sm"
                        onClick={() => handleMarkCompleted(enrollment)}
                      >
                        Complete
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleMarkIncomplete(enrollment.id)}
                      >
                        Revert
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Completion Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark as Completed</DialogTitle>
          </DialogHeader>
          
          {selectedEnrollment && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">
                  {selectedEnrollment.profiles.first_name} {selectedEnrollment.profiles.last_name}
                </p>
                <p className="text-sm text-muted-foreground">{selectedEnrollment.profiles.email}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="completion_date">Completion Date</Label>
                <Input
                  id="completion_date"
                  type="date"
                  value={completionData.completion_date}
                  onChange={(e) => setCompletionData(prev => ({
                    ...prev,
                    completion_date: e.target.value
                  }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="attendance_hours">PD Hours Earned</Label>
                <Input
                  id="attendance_hours"
                  type="number"
                  min="0"
                  step="0.5"
                  value={completionData.attendance_hours}
                  onChange={(e) => setCompletionData(prev => ({
                    ...prev,
                    attendance_hours: parseFloat(e.target.value) || 0
                  }))}
                />
                <p className="text-xs text-muted-foreground">
                  Course duration: {course?.duration_hours || 0} hours
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Add any completion notes..."
                  value={completionData.notes}
                  onChange={(e) => setCompletionData(prev => ({
                    ...prev,
                    notes: e.target.value
                  }))}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveCompletion}>
                  Mark Completed
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}